/** Call-scoped device choices shared by Electron and the browser. */
type Output = HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> };
export type CallAudioDevices = {
  devices: MediaDeviceInfo[];
  outputId: string;
  inputId: string;
  error: boolean;
};
const empty = (): CallAudioDevices => ({ devices: [], outputId: "", inputId: "", error: false });
let state = empty();
let active = false;
let generation = 0;
let changeInput: ((id: string) => Promise<void>) | undefined;
let pending: Promise<void> = Promise.resolve();
const outputs = new Set<Output>();
const listeners = new Set<() => void>();
const publish = (patch: Partial<CallAudioDevices>) => {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
};
export const getCallAudioDevices = () => state;
export function subscribeCallAudioDevices(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function canChooseCallOutput() {
  return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
}
export function callMicrophoneConstraints(): MediaTrackConstraints {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    ...(active && state.inputId ? { deviceId: { exact: state.inputId } } : {}),
  };
}
export function beginCallAudio(onInput: (id: string) => Promise<void>) {
  endCallAudio();
  active = true;
  changeInput = onInput;
  navigator.mediaDevices?.addEventListener?.("devicechange", devicesChanged);
  void refreshCallAudioDevices();
}
export function endCallAudio() {
  active = false;
  generation++;
  // A permission prompt or driver request from the previous call may never settle.
  // Its generation is invalidated; the next call must have its own operation queue.
  pending = Promise.resolve();
  changeInput = undefined;
  navigator.mediaDevices?.removeEventListener?.("devicechange", devicesChanged);
  outputs.clear();
  publish(empty());
}
function devicesChanged() {
  void refreshCallAudioDevices();
}
export async function refreshCallAudioDevices() {
  const mine = generation;
  try {
    const devices = await navigator.mediaDevices?.enumerateDevices?.();
    if (!active || mine !== generation || !devices) return;
    publish({ devices });
    if (
      state.outputId &&
      !devices.some((d) => d.kind === "audiooutput" && d.deviceId === state.outputId)
    )
      await chooseCallOutput("");
    if (
      state.inputId &&
      !devices.some((d) => d.kind === "audioinput" && d.deviceId === state.inputId)
    )
      await chooseCallInput("");
  } catch {
    if (active && mine === generation) publish({ error: true });
  }
}
function enqueue(operation: () => Promise<void>) {
  const mine = generation;
  pending = pending
    .catch(() => undefined)
    .then(async () => {
      if (!active || mine !== generation) return;
      try {
        await operation();
      } catch (error) {
        if (active && mine === generation) publish({ error: true });
        throw error;
      }
    });
  return pending;
}
export function chooseCallOutput(id: string) {
  const mine = generation;
  return enqueue(async () => {
    const previous = state.outputId;
    const targets = [...outputs];
    try {
      await Promise.all(
        targets.map(async (audio) => {
          if (audio.setSinkId) await audio.setSinkId(id);
          else if (id) throw new Error("Audio output selection is unavailable.");
        }),
      );
    } catch (error) {
      if (mine === generation)
        await Promise.allSettled(targets.map((audio) => audio.setSinkId?.(previous)));
      else await restoreCurrentOutputs();
      throw error;
    }
    if (mine !== generation) {
      await restoreCurrentOutputs();
      return;
    }
    if (active && mine === generation) publish({ outputId: id, error: false });
  });
}
function restoreCurrentOutputs() {
  return enqueue(async () => {
    await Promise.all([...outputs].map((audio) => audio.setSinkId?.(state.outputId)));
  });
}
export function chooseCallInput(id: string) {
  const mine = generation;
  return enqueue(async () => {
    await changeInput?.(id);
    if (active && mine === generation) publish({ inputId: id, error: false });
  });
}
/** Attach before play; every new speech chunk inherits the selected output. */
export function attachCallAudioOutput(audio: Output): {
  ready: Promise<void>;
  release: () => void;
} {
  if (!active) return { ready: Promise.resolve(), release: () => {} };
  outputs.add(audio);
  const ready = enqueue(async () => {
    if (outputs.has(audio) && audio.setSinkId) await audio.setSinkId(state.outputId);
  });
  return {
    ready,
    release: () => {
      outputs.delete(audio);
    },
  };
}
